import type { EventoRegistro } from '@aulaviva/shared';
import { Alert, Button, DataTable, DateRangeField, Dialog, EmptyState, JsonView, Outcome, TextField, type ColumnDef } from '@aulaviva/ui';
import { ChevronDown, ChevronUp, Download, ScrollText, ShieldCheck } from 'lucide-react';
import { useMemo, useState } from 'react';
import { urlEsportaRegistro, useRegistro, useVerificaCatena } from '@/api/amministrazione';
import { formattaDataOra } from '@/lib/date';
import { admin, amministrazione } from '@/testi';
import { CampoRicerca } from '../comuni';

const t = amministrazione.registro;

function isoGiorno(d: string, fine: boolean): string | undefined {
  if (!d) return undefined;
  const data = new Date(`${d}T${fine ? '23:59:59' : '00:00:00'}`);
  return Number.isNaN(data.getTime()) ? undefined : data.toISOString();
}

export function SchedaRegistro() {
  const [q, setQ] = useState('');
  const [azione, setAzione] = useState('');
  const [date, setDate] = useState({ da: '', a: '' });
  const [pagina, setPagina] = useState(1);
  const [aperto, setAperto] = useState<string | null>(null);
  const [dialogoEsporta, setDialogoEsporta] = useState(false);
  const [dateEsporta, setDateEsporta] = useState({ da: '', a: '' });
  const registro = useRegistro({ q: q || undefined, azione: azione || undefined, da: isoGiorno(date.da, false), a: isoGiorno(date.a, true), pagina, perPagina: 50 });
  const verifica = useVerificaCatena();

  const colonne = useMemo<ColumnDef<EventoRegistro, unknown>[]>(
    () => [
      { header: t.colonne.quando, cell: ({ row }) => <span className="whitespace-nowrap">{formattaDataOra(row.original.at)}</span> },
      { header: t.colonne.chi, cell: ({ row }) => row.original.actorNome ?? <span className="text-ink-muted">{t.sistema}</span> },
      { header: t.colonne.azione, cell: ({ row }) => <span className="t-code text-[13px]">{row.original.azione}</span> },
      { header: t.colonne.soggetto, cell: ({ row }) => (row.original.soggettoTipo ? `${row.original.soggettoTipo} · ${row.original.soggettoId ?? ''}` : '—') },
      { header: t.colonne.ip, cell: ({ row }) => <span className="t-code text-[13px]">{row.original.ip ?? '—'}</span> },
      {
        id: 'dettagli',
        header: '',
        cell: ({ row }) => {
          const e = row.original;
          const isAperto = aperto === e.id;
          return (
            <div className="flex flex-col gap-2 items-start">
              <Button variant="discreet" icon={isAperto ? ChevronUp : ChevronDown} onClick={() => setAperto(isAperto ? null : e.id)} aria-expanded={isAperto}>
                {isAperto ? t.nascondi : t.dettagli}
              </Button>
              {isAperto && <JsonView value={{ dettagli: e.dettagli, hash: e.hash, actorRole: e.actorRole, actorId: e.actorId }} className="max-w-[520px]" />}
            </div>
          );
        },
      },
    ],
    [aperto],
  );

  const dateEsportaValide = !dateEsporta.da || !dateEsporta.a || dateEsporta.a >= dateEsporta.da;
  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <p className="t-body text-ink-muted max-w-[640px]">{t.intro}</p>
        <div className="flex flex-wrap gap-3 shrink-0">
          <Button icon={ShieldCheck} onClick={() => verifica.mutate()} loading={verifica.isPending} loadingText={t.verificaInCorso}>
            {t.verifica}
          </Button>
          <Button icon={Download} onClick={() => setDialogoEsporta(true)}>
            {t.esporta}
          </Button>
        </div>
      </div>
      {verifica.isSuccess &&
        (verifica.data.integra ? (
          <Outcome variant="success" title={t.integra(verifica.data.eventi)}>
            {t.integraTesto}
          </Outcome>
        ) : (
          <Outcome variant="error" title={t.nonIntegra}>
            {t.nonIntegraTesto(verifica.data.primoErroreId)}
          </Outcome>
        ))}
      {verifica.isError && <Alert variant="error" title={verifica.error.message} />}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_240px_1fr] gap-4 items-end">
        <CampoRicerca valore={q} onCambia={(v) => { setQ(v); setPagina(1); }} hint={t.cercaAiuto} />
        <TextField label={t.filtroAzione} hint={t.filtroAzioneAiuto} mono value={azione} onChange={(e) => { setAzione(e.target.value); setPagina(1); }} />
        <DateRangeField da={date.da} a={date.a} onChange={(v) => { setDate(v); setPagina(1); }} />
      </div>
      {registro.isError && <Alert variant="error" title={registro.error.message} />}
      <DataTable
        caption={amministrazione.schede.registro}
        columns={colonne}
        data={registro.data?.elementi ?? []}
        getRowId={(e) => e.id}
        loading={registro.isPending}
        testi={admin.comuni}
        pagination={registro.data ? { pagina: registro.data.pagina, perPagina: registro.data.perPagina, totale: registro.data.totale, onPagina: setPagina } : undefined}
        empty={
          <EmptyState icon={ScrollText} title={t.nessunoTitolo}>
            {t.nessunoTesto}
          </EmptyState>
        }
      />
      <Dialog
        open={dialogoEsporta}
        onOpenChange={(o) => !o && setDialogoEsporta(false)}
        title={t.esportaTitolo}
        description={t.esportaTesto}
        actions={
          <>
            <Button onClick={() => setDialogoEsporta(false)}>{admin.comuni.annulla}</Button>
            <Button
              variant="primary"
              icon={Download}
              disabled={!dateEsportaValide}
              onClick={() => {
                window.open(urlEsportaRegistro(isoGiorno(dateEsporta.da, false), isoGiorno(dateEsporta.a, true)), '_blank', 'noopener');
                setDialogoEsporta(false);
              }}
            >
              {t.esportaConferma}
            </Button>
          </>
        }
      >
        <DateRangeField da={dateEsporta.da} a={dateEsporta.a} onChange={setDateEsporta} error={dateEsportaValide ? undefined : t.dateNonValide} />
      </Dialog>
    </>
  );
}
